import { themes } from '@toggle/design-system/src/design-tokens/themes/themes';
import { htmlNodes } from './htmlNodes';

const SVG_NS = 'http://www.w3.org/2000/svg';
export const XHTML_NS = 'http://www.w3.org/1999/xhtml';

interface InlineSvgIconsOptions {
  expandViewBox?: boolean;
  fontCss?: string;
}

export const prepareChartNodeClone = (
  chartNode: HTMLDivElement
): HTMLDivElement => {
  const clone = chartNode.cloneNode(true) as HTMLDivElement;

  Object.entries(themes.light_default).forEach(([key, value]) =>
    clone.style.setProperty(key, value)
  );

  clone.style.position = 'fixed';
  clone.style.top = '0';
  clone.style.left = '-99999px';
  clone.style.opacity = '0';
  clone.style.pointerEvents = 'none';
  clone.style.width = `${chartNode.offsetWidth}px`;
  clone.style.height = `${chartNode.offsetHeight}px`;
  document.body.appendChild(clone);

  return clone;
};

export const resetChartNodeCloneForSerialization = (clone: HTMLElement) => {
  clone.style.position = '';
  clone.style.top = '';
  clone.style.left = '';
  clone.style.opacity = '';
  clone.style.pointerEvents = '';
};

const serializeComputedStyle = (computed: CSSStyleDeclaration): string => {
  if (computed.cssText) {
    return computed.cssText;
  }

  let declaration = '';
  for (let i = 0; i < computed.length; i++) {
    const property = computed.item(i);
    if (property.startsWith('--')) {
      continue;
    }
    declaration += `${property}:${computed.getPropertyValue(property)};`;
  }
  return declaration;
};

export const inlineComputedStyles = (root: HTMLElement) => {
  const elements = [
    root,
    ...Array.from(root.querySelectorAll<HTMLElement | SVGElement>('*')),
  ];
  const declarations = elements.map(el =>
    serializeComputedStyle(window.getComputedStyle(el))
  );

  elements.forEach((el, index) => {
    if (declarations[index]) {
      el.style.cssText = declarations[index];
    }
  });
};

interface RasterizeNodeArgs {
  xhtml: string;
  width: number;
  height: number;
  fontCss?: string;
  trimPadding?: number;
}

const RASTERIZE_TIMEOUT_MS = 5_000;

const WHITE_THRESHOLD = 250;

interface ContentBounds {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export const getContentBounds = (
  // interface represents the underlying pixel data of an area of a <canvas> element.
  imageData: ImageData
): ContentBounds | null => {
  const { data, width, height } = imageData;

  let top = height;
  let left = width;
  let right = -1;
  let bottom = -1;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = (y * width + x) * 4;
      const isBackground =
        data[index] >= WHITE_THRESHOLD &&
        data[index + 1] >= WHITE_THRESHOLD &&
        data[index + 2] >= WHITE_THRESHOLD;

      if (isBackground) {
        continue;
      }

      left = Math.min(left, x);
      right = Math.max(right, x);
      top = Math.min(top, y);
      bottom = Math.max(bottom, y);
    }
  }

  if (right < left || bottom < top) {
    return null;
  }

  return { top, left, right, bottom };
};

const trimCanvasToContent = (
  canvas: HTMLCanvasElement,
  scale: number,
  paddingCssPx: number
): HTMLCanvasElement => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return canvas;
  }

  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);

  const bounds = getContentBounds(imageData);
  if (!bounds) {
    return canvas;
  }

  const pad = Math.round(paddingCssPx * scale);
  const left = Math.max(0, bounds.left - pad);
  const top = Math.max(0, bounds.top - pad);
  const right = Math.min(width - 1, bounds.right + pad);
  const bottom = Math.min(height - 1, bounds.bottom + pad);

  const croppedWidth = right - left + 1;
  const croppedHeight = bottom - top + 1;

  const output = document.createElement('canvas');
  output.width = croppedWidth;
  output.height = croppedHeight;

  const outputCtx = output.getContext('2d');
  if (!outputCtx) {
    return canvas;
  }

  outputCtx.fillStyle = '#ffffff';
  outputCtx.fillRect(0, 0, croppedWidth, croppedHeight);
  outputCtx.drawImage(
    canvas,
    left,
    top,
    croppedWidth,
    croppedHeight,
    0,
    0,
    croppedWidth,
    croppedHeight
  );

  return output;
};

export const rasterizeNodeToPng = ({
  xhtml,
  width,
  height,
  fontCss = '',
  trimPadding,
}: RasterizeNodeArgs): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!width || !height) {
      reject(new Error('Cannot rasterize node with zero width or height'));
      return;
    }

    const styleTag = fontCss ? `<style>${fontCss}</style>` : '';
    const svg =
      `<svg xmlns="${SVG_NS}" width="${width}" height="${height}">` +
      styleTag +
      `<foreignObject x="0" y="0" width="100%" height="100%">${xhtml}</foreignObject>` +
      `</svg>`;

    const image = new Image();
    let settled = false;

    const timeoutId = window.setTimeout(() => {
      if (!settled) {
        settled = true;
        reject(new Error('Rasterization timed out'));
      }
    }, RASTERIZE_TIMEOUT_MS);

    image.onload = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);

      try {
        const scale = Math.max(window.devicePixelRatio || 1, 2);
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(width * scale);
        canvas.height = Math.ceil(height * scale);

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to acquire 2d canvas context'));
          return;
        }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.setTransform(scale, 0, 0, scale, 0, 0);
        ctx.drawImage(image, 0, 0, width, height);

        const finalCanvas =
          trimPadding === undefined
            ? canvas
            : trimCanvasToContent(canvas, scale, trimPadding);

        resolve(finalCanvas.toDataURL('image/png'));
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timeoutId);
      reject(new Error('Failed to load chart snapshot for rasterizing'));
    };

    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  });

export const inlineSvgIcons = (
  root: HTMLElement,
  { expandViewBox = false, fontCss = '' }: InlineSvgIconsOptions = {}
) => {
  root.querySelectorAll('svg').forEach(svg => {
    const rect = svg.getBoundingClientRect();
    let width = rect.width;
    let height = rect.height;
    let margin = '';

    svg.style.color = window.getComputedStyle(svg).color;

    const hasText = svg.querySelector('text, tspan') !== null;
    if (fontCss && hasText) {
      const style = document.createElementNS(SVG_NS, 'style');
      style.textContent = fontCss;
      svg.insertBefore(style, svg.firstChild);
    }

    if (
      expandViewBox &&
      !svg.hasAttribute('viewBox') &&
      svg.getClientRects().length > 0
    ) {
      const bbox = svg.getBBox();
      const minX = Math.min(0, bbox.x);
      const minY = Math.min(0, bbox.y);
      const maxX = Math.max(width, bbox.x + bbox.width);
      const maxY = Math.max(height, bbox.y + bbox.height);

      if (minX < 0 || minY < 0 || maxX > width || maxY > height) {
        margin = `${minY}px ${width - maxX}px ${height - maxY}px ${minX}px`;
        width = maxX - minX;
        height = maxY - minY;
        svg.setAttribute('viewBox', `${minX} ${minY} ${width} ${height}`);
        svg.setAttribute('width', `${width}`);
        svg.setAttribute('height', `${height}`);
        svg.style.width = `${width}px`;
        svg.style.height = `${height}px`;
      }
    }

    const serialized = new XMLSerializer().serializeToString(svg);
    const img = document.createElement('img');

    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      serialized
    )}`;
    img.style.width = `${width}px`;
    img.style.height = `${height}px`;
    if (margin) {
      img.style.margin = margin;
    }

    svg.parentNode?.replaceChild(img, svg);
  });
};

interface RenderChartNodeOptions {
  width: number;
  height: number;
  fontCss?: string;
  inlineSvgOptions?: InlineSvgIconsOptions;
  prepareClone?: (clone: HTMLDivElement, source: HTMLDivElement) => void;
  trimPadding?: number;
}

export const renderChartNodeToHtml = async (
  chartNode: HTMLDivElement,
  {
    width,
    height,
    fontCss = '',
    inlineSvgOptions,
    prepareClone,
    trimPadding,
  }: RenderChartNodeOptions
): Promise<string> => {
  const clone = prepareChartNodeClone(chartNode);
  let rasterXhtml: string;
  let fallbackHtml: string;

  try {
    inlineComputedStyles(clone);
    resetChartNodeCloneForSerialization(clone);

    prepareClone?.(clone, chartNode);

    clone.setAttribute('xmlns', XHTML_NS);
    rasterXhtml = new XMLSerializer().serializeToString(clone);
    clone.removeAttribute('xmlns');

    inlineSvgIcons(clone, inlineSvgOptions);
    fallbackHtml = clone.outerHTML;
  } finally {
    document.body.removeChild(clone);
  }

  try {
    const png = await rasterizeNodeToPng({
      xhtml: rasterXhtml,
      width,
      height,
      fontCss,
      trimPadding,
    });
    return htmlNodes.img(png);
  } catch {
    return fallbackHtml;
  }
};
